import {
  Modal,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Box,
  Paper,
  createTheme,
  ThemeProvider,
} from "@mui/material";
import { useRecoilState } from "recoil";
import { supplierConsigmentDownloadState } from "../../../atoms/myShipments";
import FormatDateHour from "../../../utils/FormatDateHour";
import { useCallback } from "react";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import SimCardDownloadOutlinedIcon from "@mui/icons-material/SimCardDownloadOutlined";
import axios from "../../../api/axios";

const getFileExtension = (filePath) => {
  const cleanPath = String(filePath || "").split(/[?#]/)[0];
  const fileName = cleanPath.split("/").pop() || "";
  const extensionIndex = fileName.lastIndexOf(".");

  return extensionIndex > -1 ? fileName.slice(extensionIndex + 1).toUpperCase() : "-";
};


const SupplierConsignmentDownload = () => {
  const [supplierConsigmentDownload, setSupplierConsigmentDownload] =
    useRecoilState(supplierConsigmentDownloadState);
  const user = JSON.parse(localStorage.getItem("user"));
  const memberID = user?.user?.user_member?.id;
  const shouldShowOnlyPdfSupplierLabels = [4000000004, 4000001385].includes(
    Number(memberID)
  );
  const consignments = Array.isArray(supplierConsigmentDownload?.data)
    ? supplierConsigmentDownload.data
    : [];
  const visibleConsignments = shouldShowOnlyPdfSupplierLabels
    ? consignments
      .filter(
        (consignment) =>
          String(consignment?.label_type).trim().toLowerCase() === "label" &&
          getFileExtension(consignment?.file_path) === "PDF"
      )
    : consignments;

  const handleClose = useCallback(() => {
    setSupplierConsigmentDownload({ status: false, data: [] });
  }, [setSupplierConsigmentDownload]);

  const selectedAWB = visibleConsignments.length > 0 ? visibleConsignments[0].tracking_awb : null;

  const getFile = (path) => {
    axios
      .get(`document/get-file/${path.replaceAll("/", "-")}`, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        let blob = new Blob([response.data], {
          type: response.headers["content-type"],
        });
        let image = window.URL.createObjectURL(blob);

        window.open(image, "_blank");
      });
  };

  const theme = createTheme({
    components: {
      MuiModal: {
        styleOverrides: {
          root: {
            padding: "20px 10px",
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            padding: "10px 8px",
            fontSize: "12px",
          },
          head: {
            fontWeight: 700,
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: "10px",
          },
        },
      },
    },
  });

  return (
    <ThemeProvider theme={theme}>
      <Modal
        sx={{
          outline: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        open={supplierConsigmentDownload.status}
        onClose={handleClose}
      >
        <TableContainer
          component={Paper}
          sx={{
            outline: 0,
            width: "100%",
            maxWidth: 1100,
            margin: "0 auto",
            position: "relative",
          }}
          style={{ maxHeight: "90vh", overflowY: "auto" }}
        >
          <HighlightOffIcon
            sx={{
              position: "absolute",
              right: 4,
              top: 4,
              color: "var(--priceText)",
              cursor: "pointer",
              transition: "all 80ms linear",
              ":hover": { color: "var(--notRed)", transition: "all 80ms linear" },
            }}
            onClick={handleClose}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "var(--gap)",
              padding: "16px 16px 0",
              fontWeight: 700,
            }}
          >
            <SimCardDownloadOutlinedIcon /> Tedarikçi Konşimentosu İndir
          </Box>
          {visibleConsignments.length > 0 ? (
            <Box sx={{ padding: "0 16px 16px" }}>
              <Table aria-label="supplier-consignment-table" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ paddingLeft: "16px" }}>Tedarikçi</TableCell>
                    <TableCell>İzleme Numarası</TableCell>
                    <TableCell>Tip</TableCell>
                    <TableCell>Dosya Türü</TableCell>
                    <TableCell>Dosya</TableCell>
                    <TableCell sx={{ paddingRight: "16px" }}>Oluşturulma Tarihi</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleConsignments.map((consigment, key) => (
                    <TableRow
                      key={key}
                      sx={{
                        "&:last-child td, &:last-child th": { border: 0 },
                        backgroundColor:
                          consigment?.tracking_awb === selectedAWB ? "#a5d6a7" : "#f5f5f5",
                        color: "var(--text)",
                      }}
                    >
                      <TableCell component="th" scope="row" sx={{ paddingLeft: "16px" }}>
                        <Typography sx={{ fontWeight: 500 }}>
                          {consigment?.get_supplier?.supplier_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography>{consigment?.tracking_awb}</Typography>
                      </TableCell>
                      <TableCell>{consigment?.label_type}</TableCell>
                      <TableCell>{getFileExtension(consigment?.file_path)}</TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "8px",
                            border: "1px solid var(--inputBorderColor)",
                            borderRadius: "var(--wrapperBorderRadius)",
                            padding: "8px 10px",
                            transition: "all 150ms linear",
                            cursor: "pointer",
                            color: "inherit",
                            ":hover": {
                              backgroundColor: "var(--pastelBlue)",
                              transition: "all 150ms linear",
                            },
                          }}
                          onClick={() => getFile(consigment?.file_path)}
                        >
                          <SimCardDownloadOutlinedIcon sx={{ height: "16px" }} />
                          <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: 220 }}>
                            {consigment?.file_path}
                          </span>
                        </Box>
                      </TableCell>
                      <TableCell sx={{ paddingRight: "16px" }}>
                        {FormatDateHour(consigment?.created_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ) : (
            <Box sx={{ padding: "20px 16px 24px" }}>
              <Typography sx={{ color: "var(--priceText)" }}>
                Tedarikçi konşimentosu bulunmamaktadır.
              </Typography>
            </Box>
          )}
        </TableContainer>
      </Modal>
    </ThemeProvider>
  );
};

export default SupplierConsignmentDownload;


